import { useState } from 'react'
import { sendOverdueFollowUp } from '../../api/ariaChat'

type Money = { formatted?: string; amount?: number } | null | undefined

export type FollowUpDraft = {
  owner_key: string
  owner_name?: string | null
  to?: string | null
  email_pending?: boolean
  total?: Money
  subject?: string
  body_text?: string
  body_html?: string
  lines?: { label?: string; pay_app_id?: number }[]
  pay_app_ids?: number[]
}

export type FollowUpDigest = {
  overdue_total?: Money
  drafts?: FollowUpDraft[] | null
}

function moneyText(m: Money): string {
  if (m?.formatted) return m.formatted
  if (m?.amount != null) {
    return `$${Number(m.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  return '$0.00'
}

function DraftBlock({ draft }: { draft: FollowUpDraft }) {
  const [awaiting, setAwaiting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const lines = Array.isArray(draft.lines) ? draft.lines : []

  async function copyDraft() {
    const text = draft.body_text || ''
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setNotice('Copied')
  }

  async function confirmSend() {
    if (!awaiting) {
      setAwaiting(true)
      setNotice('Tap Confirm send to email the owner. Nothing has been sent.')
      return
    }
    try {
      const out = await sendOverdueFollowUp({ owner_key: draft.owner_key, confirm: true })
      setNotice(out.message || 'Sent')
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Send failed')
    } finally {
      setAwaiting(false)
    }
  }

  return (
    <article className="soren-followup-draft" data-testid="soren-followup-draft">
      <h3>{draft.owner_name || 'Owner'} · {moneyText(draft.total)}</h3>
      <p className="soren-followup-subject">{draft.subject}</p>
      <ul className="soren-fact-list">
        {lines.map((line) => (
          <li key={line.pay_app_id ?? line.label} data-testid="soren-followup-line">
            {line.label}
          </li>
        ))}
      </ul>
      <pre data-testid="soren-followup-body">{draft.body_text}</pre>
      <div className="soren-followup-actions">
        <button
          type="button"
          className="cr-btn"
          data-testid="soren-followup-copy"
          onClick={() => void copyDraft()}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          type="button"
          className="cr-btn dark"
          data-testid={awaiting ? 'soren-followup-confirm-send' : 'soren-followup-send'}
          disabled={!!draft.email_pending}
          onClick={() => void confirmSend()}
        >
          {awaiting ? 'Confirm send' : 'Send with my approval'}
        </button>
      </div>
      {notice ? <p data-testid="soren-followup-notice">{notice}</p> : null}
    </article>
  )
}

export default function SorenFollowUpDraftsCard({ digest }: { digest?: FollowUpDigest | null }) {
  const drafts = Array.isArray(digest?.drafts) ? digest.drafts : []
  return (
    <div className="soren-followup-answer" data-testid="soren-followup-answer">
      <p data-testid="soren-followup-total">
        Overdue follow-ups · {moneyText(digest?.overdue_total)}
      </p>
      {drafts.map((d) => (
        <DraftBlock key={d.owner_key} draft={d} />
      ))}
    </div>
  )
}
